import { action, observable } from 'mobx';
import { api } from '../config';

// Axios (used for loading static translation file)
import axios from 'axios';

// import DomainStore
import DomainStore from './DomainStore'

// Helpers
import { getData, storeData } from '../helpers/helpers';

let obx = observable({
  /*-------------------------------------------
    Token/user data
  -------------------------------------------*/
  token: null,
  userData: null,
  iam: null,

  /**
   * checkToken - Checks the user token and gets permissions and user data
   */
  checkToken: action(function(){
    obx.startLoading('checkToken');

    let headers = {
      'token': getData('token')
    };

    Promise.all([
      api.get('/check', { headers }),
      api.get('/permissions', { headers })
    ])
      .then(response => {
        storeData('userData', JSON.stringify(response[0].data));
        storeData('iam', JSON.stringify(response[1].data));

        obx.userData = response[0].data;
        obx.iam = response[1].data;

        obx.finishLoading('checkToken');
      })
  }),

  /*-------------------------------------------
    Loading
  -------------------------------------------*/
  loadingCalls: [],
  loading: false,
  loadingClass: '',

  /**
	 * startLoading - Sets loading state to true and adds the provided request to the array of current requests
   * @param {string} request - The name of the request being loaded
	 */
  startLoading: action(function(request) {
    obx.loading = true;
    obx.loadingCalls.push(request)
  }),

  /**
	 * finishLoading - Removes the provided request from the array of requests and sets the loading state to false if the request array is empty
   * @param {string} request - The name of the request being loaded
	 */
  finishLoading: action(function(request) {
    const requestIndex = obx.loadingCalls.indexOf(request);

    if(requestIndex >= 0) {
      obx.loadingCalls.splice(requestIndex, 1);
    } else {
      obx.loadingCalls.length = 0;
    }

    if(obx.loadingCalls.length === 0 || typeof request === 'undefined') {
      obx.loadingClass = 'leave';
      obx.loading = false;

      // Wait for loader to fade out before removing
      setTimeout(function(){
        obx.loadingClass = '';
        obx.loadingMessage = null;
      }, 250);
    }
  }),

  /*-------------------------------------------
    Error handling
  -------------------------------------------*/
  /**
	 * throwError - Determines whether to show a specific or general error message
   * @param {object} error
	 */
  throwError: action(function(error){
    obx.finishLoading();

    console.error(error);

    if(typeof error.response !== 'undefined') {
      // API errors will return an error object with a response
      // which means we have a message for them
      obx.showErrorMsg(error.response.data);
    } else {
      // If there’s no response with the error, it’s a server
      // connection error (i.e. axios can’t reach the URL)
      obx.showErrorMsg();
    }
  }),

  /**
	 * showErrorMsg - Shows a pop-up dialog with the appropriate error message for a provided error
   * @param {object} data - The error data
   * @param {string} data.code - The error code
	 */
  showErrorMsg: action(function(data){
    obx.dialog = true;
    obx.dialogContent.heading = obx.translation.Errors.heading;
    obx.dialogContent.button = obx.translation.Errors.button;
    obx.dialogType = null;
    obx.dialogSubmit = null;
    obx.dialogCallback = null;

    if(!data) {
      // Server error (API call returned no data)
      obx.dialogContent.body = obx.translation.Errors[1301].message;
    } else {
      // API call returned data
      if(typeof obx.translation.Errors[data.code] !== 'undefined') {
        // Error translation exists
        obx.dialogContent.body = obx.translation.Errors[data.code].message;
      } else {
        // Error translation does not exist
        console.warn('Error code not present in translation file. Falling back to API response message');
        obx.dialogContent.body = data.message;
      }
    }

    // Redirect expired session
    if(data && data.code === '5002') {
      window.location.href = '/#/session-expired';
    }
  }),

  /*-------------------------------------------
    Translation
  -------------------------------------------*/
  locale: 'en',
  translation: null,
  translationLoaded: false,

  /**
	 * getTranslation - Loads the appropriate translation file
   * @param {string} locale
	 */
  getTranslation: action(function(locale){
    obx.translationLoaded = true;

    const timestamp = (new Date()).getTime();

    obx.startLoading('getTranslation');

    axios.get(`/json/${obx.locale}.json?t=${timestamp}`)
      .then(response => {
        obx.translation = response.data.content;
        obx.finishLoading('getTranslation');
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),

  /*-------------------------------------------
    Time zones
  -------------------------------------------*/
  timeZoneList: null,
  timeZone: null,
  timeZoneSid: null,

  /**
   * getUserTimeZone - Uses the Intl API to determine the client machine’s time zone
   */
  getUserTimeZone: action(function(){
    obx.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    obx.getServerTimeZones();
  }),

  /**
   * getServerTimeZones - Gets a list of time zones with SIDs from the server
   */
  getServerTimeZones: action(function(){
    obx.startLoading('getServerTimeZones');

    api.get('/timeZones')
      .then(response => {
        obx.timeZoneList = response.data.results;

        // Determine the SID of the client machine’s time zone
        const serverTimeZone = obx.timeZoneList.filter(zone => zone.name === obx.timeZone);

        if(serverTimeZone.length > 0) {
          obx.timeZoneSid = serverTimeZone[0].sid;
        }

        obx.finishLoading('getServerTimeZones');
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),

  currentTime: null,
  getCurrentTime: action(function() {
    obx.startLoading('getCurrentTime')

    api.get('/currentTime')
        .then(response => {
          obx.currentTime = response.data.currentTime;
        })
      obx.finishLoading('getCurrentTime');
  }),

  /*-------------------------------------------
    Language
  -------------------------------------------*/
  language: getData('language') || 'en',
  languagesList: [],
  /**
   * setLanguage - Set exam and system language
   * @param text languageCode
   */
  setLanguage: action(function(languageCode){
    obx.language = languageCode
    storeData('language', languageCode)
    //todo: we should set the locale here as well once we have translations
  }),

  /**
   * listLanguages - loads an array of exam languages into store
   */
  listLanguages: action(function(){
    axios.get(`/json/languages.json`)
        .then(response => {
          const {domain} = DomainStore
          const {data} = response;
          obx.languagesList = data[domain] || [];
        })
        .catch(error => {
          obx.throwError(error);
        });
  }),

  /*-------------------------------------------
    Dialog box
  -------------------------------------------*/
  dialog: false,
  dialogType: null,
  dialogContent: {},
  dialogCallback: null,
  dialogSubmit: null,

  /**
	 * showDialog - Shows a pop-up dialog box with the provided content
   * @param {object} content
	 */
  showDialog: action(function(content, type){
    if(content) {
      obx.dialogContent = content;
    }

    obx.dialog = true;

    if(type) {
      obx.dialogType = type;
    }

    // Focus the dialog
    const dialogTitle = document.querySelector('.dialog__title');

    if(dialogTitle) {
      dialogTitle.focus();
    }
  }),

  /**
	 * closeDialog - Closes the pop-up dialog box
   * @param {object} content
	 */
  closeDialog: action(function(){
    obx.dialog = false;
    obx.dialogType = null;
    obx.dialogContent = {};

    // Focus the main content element
    const main = document.querySelector('#main-content');

    if(main) {
      main.focus();
    }

    // Dialog callback
    if(obx.dialogCallback) {
      obx.dialogCallback();
      obx.dialogCallback = null;
    }
  }),

  /*-------------------------------------------
    Toast
  -------------------------------------------*/
  toast: null
});

export default obx;
